// Interface do celular (pedido do Arthur, 15/10, com o Huntera como referência), no visual do cliente do Tibia:
// barra de cima (avatar, Noxara Coins, loja, online e o menu de 3 traços), o jogo ocupando todo o meio, e embaixo os
// botões grandes de atalho, vida/mana/level/capacidade, mochila/pouch/chat/análise e a barra de magias em espaços grandes.
import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import type { HuntInfo } from './LeftPanel';
import type { CharacterState } from '../../../../shared/src/protocol';
import { expForLevel } from '../../../../shared/src/game/experience';
import { OutfitImage } from '../../screens/Characters';

const IMG = '/assets/interface/images';

export function MobileTop(props: { c: CharacterState | null; coins: number; online: number | null; onAvatar: () => void; onStore: () => void; onMenu: () => void }) {
  return (
    <header class="m-top">
      <button class="m-avatar" onClick={props.onAvatar} title="Personagem e inventário">
        {props.c ? <OutfitImage outfit={props.c.outfit} scale={0.75} /> : null}
      </button>
      <button class="m-coins" onClick={props.onStore} title="Noxara Coins (abrir a loja)">
        <img src={`${IMG}/icon-tibiacoin.png`} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        <b>{props.coins.toLocaleString('pt-BR')}</b>
        <i>+</i>
      </button>
      <button class="m-store" onClick={props.onStore} title="Noxara Store"><img src={`${IMG}/button-store-small-up.png`} alt="Loja" /></button>
      <span class="m-online">{props.online !== null ? <><i class="dot" />{props.online.toLocaleString('pt-BR')}</> : null}</span>
      <button class="m-burger" onClick={props.onMenu} title="Menu" aria-label="Menu"><span /><span /><span /></button>
    </header>
  );
}

export interface QuickButton { id: string; label: string; onClick: () => void; on?: boolean; disabled?: boolean }

export function MobileBottom(props: {
  c: CharacterState | null; freeCap: number | null; quick: QuickButton[]; hotbar: ComponentChildren;
  onBag: () => void; onPouch: () => void; onChat: () => void; chat: boolean; onPanels: () => void; unread?: boolean;
}) {
  const c = props.c;
  const pct = (v: number, m: number) => (m > 0 ? Math.max(0, Math.min(100, (v / m) * 100)) : 0);
  const lvlPct = c ? (() => { const a = expForLevel(c.level), b = expForLevel(c.level + 1); return b > a ? ((c.experience - a) / (b - a)) * 100 : 0; })() : 0;
  return (
    <footer class="m-bottom">
      <div class="m-quick">
        {props.quick.map((q) => (
          <button key={q.id} class={'m-qbtn' + (q.on ? ' on' : '')} disabled={q.disabled} onClick={q.onClick}>{q.label}</button>
        ))}
      </div>
      <div class="m-row">
        <div class="m-bars">
          <div class="m-bar hp"><i style={{ width: pct(c?.health ?? 0, c?.maxHealth ?? 1) + '%' }} /><span>{c ? `${c.health}/${c.maxHealth}` : ''}</span></div>
          <div class="m-bar mp"><i style={{ width: pct(c?.mana ?? 0, c?.maxMana ?? 1) + '%' }} /><span>{c ? `${c.mana}/${c.maxMana}` : ''}</span></div>
          <div class="m-bar lv"><i style={{ width: lvlPct + '%' }} /><span><b>Lv {c?.level ?? ''}</b><em>{lvlPct.toFixed(1)}%</em></span></div>
          <div class="m-bar cap"><i style={{ width: pct(props.freeCap ?? 0, Math.max(props.freeCap ?? 0, c?.capacity ?? 1)) + '%' }} /><span>{props.freeCap !== null ? `${props.freeCap.toLocaleString('pt-BR')} oz` : ''}</span></div>
        </div>
        <div class="m-right">
          <div class="m-icons">
            <button onClick={props.onBag} title="Personagem e mochila"><img src={`${IMG}/inventory-back.png`} alt="Mochila" /></button>
            <button onClick={props.onPouch} title="Noxara Store Box" class="m-pouch"><span class="sb-ico" />Store Box</button>
            <button onClick={props.onChat} title="Chat" class={'m-chatbtn' + (props.chat ? ' on' : '') + (props.unread ? ' unread' : '')}>💬</button>
            <button onClick={props.onPanels} title="Hunt Analyser, Loot e Bestiário" class="m-anabtn">📊</button>
          </div>
          {props.hotbar}
        </div>
      </div>
    </footer>
  );
}

/** números curtos para o celular: 151.950 → 152k, 2.400.000 → 2,4kk */
const short = (n: number) => {
  const a = Math.abs(n), s = n < 0 ? '-' : '';
  if (a >= 1_000_000) return s + (a / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: a >= 10_000_000 ? 0 : 1 }) + 'kk';
  if (a >= 10_000) return s + Math.round(a / 1000).toLocaleString('pt-BR') + 'k';
  return s + Math.round(a).toLocaleString('pt-BR');
};

/** Hunt Analyser compacto por cima do jogo no celular: toque abre o completo, o "–" recolhe num botãozinho */
export function MiniAnalyser(props: { h: HuntInfo | null; onOpen: () => void }) {
  const [min, setMin] = useState(() => { try { return localStorage.getItem('nx-mini-ha') === '1'; } catch { return false; } });
  const toggle = (v: boolean) => { setMin(v); try { localStorage.setItem('nx-mini-ha', v ? '1' : '0'); } catch { /* sem storage */ } };
  const h = props.h;
  if (!h) return null;
  if (min) return <button class="mini-ha min" onClick={() => toggle(false)} title="Mostrar o Hunt Analyser">📊</button>;
  const bal = h.balanceHour ?? h.loot - h.waste;
  return (
    <div class="mini-ha" onClick={props.onOpen} title="Abrir o Hunt Analyser completo">
      <button class="mini-ha-x" onClick={(e) => { e.stopPropagation(); toggle(true); }} aria-label="Recolher">–</button>
      <div><span>Tempo</span><b>{h.elapsed}</b></div>
      <div><span>XP/h</span><b class="ha-gold">{short(h.xpHour)}</b></div>
      <div><span>Loot</span><b class="ha-green">{short(h.loot)}</b></div>
      <div><span>{h.balanceHour !== undefined ? 'Bal/h' : 'Bal'}</span><b class={bal >= 0 ? 'ha-green' : 'ha-red'}>{short(bal)}</b></div>
      <div><span>Mortos</span><b class="ha-blue">{short(h.kills)}</b></div>
    </div>
  );
}
